/*
 * Ferramentas Esthétique: calculadora de receituário, orçamento de carteira e comparativos.
 * As funções puras (PetgrafTools.*) não tocam no DOM e são testadas em pg-tools.test.mjs.
 * Os preços vêm do HTML (Liquid lê as variantes no servidor); aqui só se recalcula.
 */
(function (root) {
  'use strict';

  const UNITS = {
    unidade: ['unidade', 'unidades'], unidades: ['unidade', 'unidades'],
    bloco: ['bloco', 'blocos'], blocos: ['bloco', 'blocos'],
    folha: ['folha', 'folhas'], folhas: ['folha', 'folhas'],
    'talão': ['talão', 'talões'], 'talões': ['talão', 'talões'],
    'ímã': ['ímã', 'ímãs'], 'ímãs': ['ímã', 'ímãs'],
    carteira: ['carteira', 'carteiras'], carteiras: ['carteira', 'carteiras'],
    'cartão': ['cartão', 'cartões'], 'cartões': ['cartão', 'cartões'],
    'lâmina': ['lâmina', 'lâminas'], 'lâminas': ['lâmina', 'lâminas'],
  };

  /** "25 blocos" → {count:25, unit:'bloco', plural:'blocos'}; "06 blocos (300x2v)" → count 6, detail. */
  function parseRun(title) {
    const match = String(title == null ? '' : title).trim().match(/^(\d[\d.]*) (\S+)(?: soltas?)?(?: \((.+)\))?$/);
    if (!match) return null;
    const words = UNITS[match[2].toLowerCase()];
    const count = Number(match[1].replace(/\./g, ''));
    if (!words || !Number.isSafeInteger(count) || count <= 0) return null;
    return { count, unit: words[0], plural: words[1], detail: match[3] || '' };
  }

  /** Variantes com contagem válida, em ordem crescente de quantidade. */
  function runs(variants) {
    return (variants || [])
      .map(variant => ({ ...variant, ...(parseRun(variant.title) || {}) }))
      .filter(variant => Number.isSafeInteger(variant.count) && Number.isSafeInteger(variant.price) && variant.price > 0)
      .sort((a, b) => a.count - b.count);
  }

  /** Quantidades com inversão Lite/Trio ficam fora de qualquer cálculo de carteiras. */
  function conflictingCounts(products) {
    const trio = runs(products.find(product => product.role === 'trio')?.variants);
    const lite = runs(products.find(product => product.role === 'lite')?.variants);
    return trio.filter(t => lite.some(l => l.count === t.count && l.price < t.price)).map(t => t.count);
  }

  function safePick(variants, need, tolerance, excluded = []) {
    const pick = pickVariant(variants, need, tolerance);
    return pick && !excluded.includes(pick.variant.count) ? pick : null;
  }

  /** Legado Shopify: um bloco para impressora representa 100 folhas soltas. */
  function sheetVariant(product, variant) {
    if (!variant || !product.role?.startsWith('impressora')) return variant;
    if (variant.unit === 'folha') return variant;
    if (variant.unit !== 'bloco' || !(product.perBlock > 0)) return null;
    return { ...variant, count: variant.count * product.perBlock, unit: 'folha', plural: 'folhas' };
  }

  /**
   * Menor variante disponível que cubra ao menos `tolerance` da necessidade.
   * Se nenhuma cobrir, devolve a maior disponível com `short: true` (a tela manda para o WhatsApp).
   */
  function pickVariant(variants, need, tolerance) {
    const list = runs(variants).filter(variant => variant.available !== false);
    if (!list.length) return null;
    const ratio = Number.isFinite(tolerance) && tolerance > 0 && tolerance <= 1 ? tolerance : 0.9;
    const target = Number.isFinite(need) && need > 0 ? need * ratio : 0;
    const found = list.find(variant => variant.count >= target - 1e-9);
    if (found) return { variant: found, short: false };
    return { variant: list[list.length - 1], short: true };
  }

  /** Mesma regra de offer() em pg-quantity-options: próxima quantidade maior com preço unitário menor. */
  function offer(variants, selected) {
    if (!selected || !selected.count || !Number.isSafeInteger(selected.price) || selected.price <= 0) return null;
    return runs(variants).filter(option => option.available !== false && option.unit === selected.unit
      && option.count > selected.count && option.price > selected.price
      && option.price / option.count < selected.price / selected.count)[0] || null;
  }

  /** Quanto cada unidade de `cheaper` sai mais barata que em `base`, em % inteiro arredondado para baixo. */
  function unitSaving(base, cheaper) {
    if (!base || !cheaper || !base.count || !cheaper.count) return 0;
    const whole = base.price * cheaper.count;
    return whole > 0 ? Math.floor((whole - cheaper.price * base.count) * 100 / whole) : 0;
  }

  /** Primeiro número de "100 folhas por bloco", "100 vias", "10 imunizações". */
  function leadingNumber(text) {
    const match = String(text == null ? '' : text).match(/(\d[\d.]*)/);
    if (!match) return 0;
    const value = Number(match[1].replace(/\./g, ''));
    return Number.isFinite(value) ? value : 0;
  }

  /** Receituário: folhas e blocos por mês, necessidade do período. */
  function receituario({ consultas, receitas, dias, meses, perBlock }) {
    const sheetsMonth = consultas * receitas * dias;
    const pages = perBlock > 0 ? perBlock : 100;
    const blocksMonth = sheetsMonth / pages;
    return { sheetsMonth, blocksMonth, need: blocksMonth * meses, perBlock: pages };
  }

  /** Meses que `count` blocos duram no ritmo informado. */
  function monthsCovered(count, blocksMonth) {
    return blocksMonth > 0 ? count / blocksMonth : 0;
  }

  /**
   * Anos de registro de uma carteira: o 1º ano consome `firstYear` espaços,
   * cada ano seguinte consome `boosters`.
   */
  function yearsOfRecord(capacity, firstYear, boosters) {
    if (!(capacity > 0) || !(firstYear > 0)) return 0;
    if (capacity <= firstYear) return capacity / firstYear;
    if (!(boosters > 0)) return Infinity;
    return 1 + (capacity - firstYear) / boosters;
  }

  /** Carteiras: necessidade do período e anos de registro de um modelo. */
  function carteira({ pacientes, meses, capacity, firstYear, boosters }) {
    return { need: pacientes * meses, years: yearsOfRecord(capacity, firstYear, boosters) };
  }

  /** Custo por espaço de vacina, em centavos (fracionário). */
  function perSlot(price, count, capacity) {
    return count > 0 && capacity > 0 ? price / count / capacity : 0;
  }

  /** "cao-essencial:4:2|gato:3:2" → [{id, first, boosters}] */
  function parseProtocols(text) {
    return String(text || '').split('|').map(entry => entry.trim().split(':'))
      .filter(parts => parts.length === 3 && /^[a-z0-9-]+$/.test(parts[0]))
      .map(([id, first, boosters]) => ({ id, first: Number(first), boosters: Number(boosters) }))
      .filter(item => Number.isSafeInteger(item.first) && item.first > 0 && Number.isSafeInteger(item.boosters) && item.boosters >= 0);
  }

  /** Lê um número da URL dentro dos limites; fora deles, volta ao padrão. */
  function readNumber(params, name, fallback, min, max) {
    if (!params || !params.has(name)) return fallback;
    const raw = String(params.get(name)).replace(',', '.');
    if (!/^\d+(\.\d+)?$/.test(raw)) return fallback;
    const value = Number(raw);
    return value >= min && value <= max ? value : fallback;
  }

  function readChoice(params, name, fallback, allowed) {
    if (!params || !params.has(name)) return fallback;
    const value = params.get(name);
    return allowed.includes(value) ? value : fallback;
  }

  const moneyFormat = typeof Intl !== 'undefined' ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }) : null;
  /** Centavos → "R$ 804,90" (com espaço comum, não o NBSP do Intl). */
  function money(cents) {
    return moneyFormat.format(Math.round(cents) / 100).replace(/ /g, ' ');
  }
  /** 5.72 → "5,7"; 4 → "4". */
  function decimal(value, digits) {
    const places = digits == null ? 1 : digits;
    return new Intl.NumberFormat('pt-BR', { maximumFractionDigits: places, minimumFractionDigits: 0 }).format(value);
  }
  function plural(count, one, many) { return count === 1 ? one : many; }
  /** Quantidade legível: "1.000 unidades". */
  function runLabel(variant) {
    return new Intl.NumberFormat('pt-BR').format(variant.count) + ' ' + plural(variant.count, variant.unit, variant.plural);
  }
  function yearsLabel(years) {
    if (!Number.isFinite(years) || years > 15) return 'mais de 15 anos';
    if (years < 1) return 'menos de 1 ano';
    const rounded = Math.floor(years * 2) / 2;
    return decimal(rounded, 1) + ' ' + plural(rounded, 'ano', 'anos');
  }

  /** Faixa sem dado pessoal para analytics: 17 → "11-20". */
  function band(value) {
    if (!(value > 0)) return '0';
    const bands = [5, 10, 20, 40, 80, 160];
    let low = 1;
    for (const high of bands) { if (value <= high) return low + '-' + high; low = high + 1; }
    return low + '+';
  }

  const api = { conflictingCounts, safePick, sheetVariant, parseRun, runs, pickVariant, offer, unitSaving, leadingNumber, receituario, monthsCovered, yearsOfRecord, carteira, perSlot, parseProtocols, readNumber, readChoice, money, decimal, runLabel, yearsLabel, band };
  root.PetgrafTools = api;
  if (typeof module !== 'undefined') module.exports = api;

  if (typeof customElements === 'undefined' || customElements.get('pg-tool')) return;

  customElements.define('pg-tool', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      const source = this.querySelector('script[data-tool-data]');
      if (!source) return;
      try { this.data = JSON.parse(source.textContent); } catch { return; }
      this.ready = true;
      this.abort = new AbortController();
      this.kind = this.data.tool;
      this.products = (this.data.products || []).filter(product => product && product.variants && product.variants.length);
      this.excluded = conflictingCounts(this.products);
      this.override = null;
      this.touched = false;
      this.dataset.live = '';
      const { signal } = this.abort;
      this.addEventListener('input', event => this.changed(event), { signal });
      this.addEventListener('change', event => this.changed(event), { signal });
      this.addEventListener('click', event => this.clicked(event), { signal });
      this.querySelectorAll('input[name="quantidade"]').forEach(input => {
        if (this.excluded.includes(Number(input.value))) { input.disabled = true; input.closest('label').hidden = true; }
      });
      this.restore();
      this.render();
    }

    disconnectedCallback() { this.ready = false; this.abort?.abort(); cancelAnimationFrame(this.frame); clearTimeout(this.announceTimer); clearTimeout(this.shareTimer); }

    field(name) { return this.querySelector('[data-field="' + name + '"]'); }

    value(name) {
      const input = this.field(name);
      if (!input) return 0;
      const number = Number(String(input.value).replace(',', '.'));
      const min = Number(input.min), max = Number(input.max);
      if (!Number.isFinite(number)) return Number(input.defaultValue) || min || 0;
      const bounded = Math.min(Math.max(number, Number.isFinite(min) ? min : number), Number.isFinite(max) ? max : number);
      const step = Number(input.step) || 1;
      return Number((min + Math.round((bounded - min) / step) * step).toFixed(6));
    }

    choice(name) { return this.querySelector('input[name="' + name + '"]:checked')?.value || ''; }

    setChoice(name, value) {
      const input = [...this.querySelectorAll('input[name="' + name + '"]')].find(item => item.value === value);
      if (input && !input.disabled) input.checked = true;
    }

    /** Estado inicial pela URL; parâmetro inválido volta ao padrão do editor de temas. */
    restore() {
      const params = new URLSearchParams(location.search);
      this.querySelectorAll('input[data-field][data-param]').forEach(input => {
        const fallback = Number(input.defaultValue);
        input.value = String(readNumber(params, input.dataset.param, fallback, Number(input.min), Number(input.max)));
        input.value = String(this.value(input.dataset.field));
      });
      this.querySelectorAll('[data-choice-param]').forEach(group => {
        const name = group.dataset.choiceParam;
        const options = [...group.querySelectorAll('input[type=radio]:not(:disabled)')];
        const fallback = options.find(option => option.defaultChecked)?.value || options[0]?.value;
        this.setChoice(options[0]?.name, readChoice(params, name, fallback, options.map(option => option.value)));
      });
      if (this.kind === 'carteira' && (params.has('doses') || params.has('reforcos'))) this.syncProtocol(false);
      if (this.kind === 'carteira' && !params.has('doses') && !params.has('reforcos')) this.applyProtocol(this.choice('protocolo'));
    }

    changed(event) {
      const target = event.target;
      if (target.matches('[data-field="doses"],[data-field="reforcos"]')) this.syncProtocol(true);
      if (target.name === 'protocolo' && event.type === 'change') this.applyProtocol(target.value);
      if (target.matches('input[data-field]') && event.type === 'change') {
        const clean = this.value(target.dataset.field);
        if (String(clean) !== target.value) target.value = String(clean);
      }
      if (!target.matches('[name="modelo"]')) this.override = null;
      this.schedule(true);
    }

    clicked(event) {
      const step = event.target.closest('[data-step]');
      if (step) {
        const input = this.field(step.dataset.target);
        if (!input) return;
        const delta = Number(input.step) || 1;
        const next = this.value(step.dataset.target) + (step.dataset.step === 'up' ? delta : -delta);
        const min = Number(input.min), max = Number(input.max);
        input.value = String(Math.round(Math.min(Math.max(next, min), max) * 10) / 10);
        input.dispatchEvent(new Event('input', { bubbles: true }));
        return;
      }
      const upgrade = event.target.closest('[data-offer-choose]');
      if (upgrade) { this.override = upgrade.value; this.schedule(true); return; }
      const share = event.target.closest('[data-tool-share]');
      if (share) { this.share(share); return; }
      const product = event.target.closest('a[data-tool-product]');
      if (product) this.metric('product_click', { handle: product.dataset.toolProduct });
      const add = event.target.closest('[data-kit-add]');
      // Recalcula na hora se ainda há um quadro pendente: o painel lê a variante logo depois deste clique.
      if (add && this.frame) { cancelAnimationFrame(this.frame); this.frame = null; this.render(true); }
      if (add && (add.disabled || !this.current)) { event.preventDefault(); event.stopPropagation(); return; }
      if (add && !add.disabled && !add.hasAttribute('data-cart-review')) this.metric('add', { handle: this.current?.handle || '', variant_title: this.current?.variant || '' });
    }

    applyProtocol(id) {
      const protocol = parseProtocols(this.data.protocols).find(item => item.id === id);
      if (!protocol) return;
      const doses = this.field('doses'), boosters = this.field('reforcos');
      if (doses) doses.value = String(protocol.first);
      if (boosters) boosters.value = String(protocol.boosters);
    }

    /** Doses digitadas à mão: marca o protocolo correspondente ou "personalizado". */
    syncProtocol() {
      const first = this.value('doses'), boosters = this.value('reforcos');
      const match = parseProtocols(this.data.protocols).find(item => item.first === first && item.boosters === boosters);
      this.setChoice('protocolo', match ? match.id : 'personalizado');
    }

    schedule(byUser) {
      if (byUser && !this.touched) { this.touched = true; this.metric('calculate', this.bands()); }
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => { this.frame = null; this.render(byUser); });
    }

    bands() {
      const out = {};
      this.querySelectorAll('input[data-field][data-param]').forEach(input => { out[input.dataset.param] = band(this.value(input.dataset.field)); });
      return out;
    }

    render(byUser) {
      if (this.kind === 'receituario') this.renderReceituario();
      else if (this.kind === 'carteira') this.renderCarteira();
      else if (this.kind === 'comparativo') this.renderComparativo();
      if (byUser) this.syncURL();
    }

    text(name, value) {
      this.querySelectorAll('[data-out="' + name + '"]').forEach(node => { if (node.textContent !== value) node.textContent = value; });
    }

    show(name, visible) { this.querySelectorAll('[data-show="' + name + '"]').forEach(node => { node.hidden = !visible; }); }

    /** Atualiza a variante do carrinho (pg-kit do painel) e o resumo fixo. */
    setCart(product, variant, short) {
      const kit = this.querySelector('pg-kit');
      if (!kit) return;
      const item = kit.querySelector('[data-kit-item]');
      const button = kit.querySelector('[data-kit-add]');
      kit.hidden = false;
      this.show('unavailable', false);
      const changed = item.value !== String(variant.id);
      item.value = String(variant.id);
      kit.dataset.kitId = 'tool-' + this.kind;
      if (changed && button.hasAttribute('data-cart-review')) {
        button.removeAttribute('data-cart-review');
        button.firstChild.textContent = kit.dataset.kitAddLabel + ' ';
        const status = kit.querySelector('[data-kit-status]');
        status.textContent = '';
        status.dataset.inCart = 'false';
      }
      button.disabled = variant.available === false;
      this.text('bar-run', runLabel(variant));
      this.text('bar-name', product.name);
      kit.querySelector('pg-installment-price')?.setAttribute('amount', String(variant.price));
      this.show('whatsapp', short);
      this.current = { handle: product.handle, variant: variant.title };
      const link = this.querySelector('[data-out-link="product"]');
      if (link) { link.hidden = false; link.href = product.url + '?variant=' + variant.id; link.dataset.toolProduct = product.handle; }
      this.announce(runLabel(variant) + ' de ' + product.name + ', ' + money(variant.price));
    }

    announce(message) {
      const live = this.querySelector('[data-tool-live]');
      if (!live) return;
      clearTimeout(this.announceTimer);
      this.announceTimer = setTimeout(() => { live.textContent = message; }, 500);
    }

    chosen(product, need) {
      const list = runs(product.variants).filter(v => v.available !== false && !this.excluded.includes(v.count));
      const forced = this.override && list.find(variant => String(variant.id) === String(this.override));
      if (forced) return { variant: forced, short: false, forced: true };
      return safePick(product.variants, need, this.data.tolerance, this.excluded);
    }

    renderOffer(product, variant) {
      const next = offer(product.variants, variant);
      if (next && this.excluded.includes(next.count)) { this.show('offer', false); return; }
      this.show('offer', Boolean(next));
      if (!next) return;
      const extra = next.count - variant.count;
      this.text('offer', 'Mais ' + new Intl.NumberFormat('pt-BR').format(extra) + ' ' + plural(extra, variant.unit, variant.plural) + ' por ' + money(next.price - variant.price) + '.' + (unitSaving(variant, next) >= 5 ? ' Cada ' + variant.unit + ' sai ' + unitSaving(variant, next) + '% mais ' + (/^(carteira|folha|unidade|lâmina)$/.test(variant.unit) ? 'barata' : 'barato') + '.' : ''));
      const button = this.querySelector('[data-offer-choose]');
      if (button) { button.value = String(next.id); button.textContent = 'Escolher ' + runLabel(next); }
    }

    renderReceituario() {
      const product = this.products.find(item => item.role === this.choice('material')) || this.products[0];
      if (!product) return;
      const inputs = { consultas: this.value('consultas'), receitas: this.value('receitas'), dias: this.value('dias'), meses: this.value('meses') };
      const result = receituario({ ...inputs, perBlock: product.perBlock });
      const pick = this.chosen(product, result.need);
      if (!pick) { this.clearCart(); return; }
      const { variant } = pick;
      const covered = monthsCovered(variant.count, result.blocksMonth);
      this.text('sheets-month', decimal(result.sheetsMonth, 0));
      this.text('blocks-month', decimal(result.blocksMonth, 1));
      this.text('need', decimal(result.need, 1));
      this.text('months', decimal(inputs.meses, 0) + ' ' + plural(inputs.meses, 'mês', 'meses'));
      this.text('per-block-text', product.perBlockText || (result.perBlock + ' folhas'));
      this.text('run', runLabel(variant));
      this.text('covered', covered >= 1 ? 'cerca de ' + decimal(covered, 1) + ' ' + plural(Math.round(covered * 10) / 10, 'mês', 'meses') : 'menos de 1 mês');
      this.text('unit-price', money(variant.price / variant.count) + ' por bloco');
      this.text('sheet-price', money(variant.price / variant.count / result.perBlock) + ' por folha');
      this.renderOffer(product, variant);
      this.setCart(product, variant, pick.short);
    }

    renderCarteira() {
      const pacientes = this.value('pacientes'), meses = this.value('meses');
      const first = this.value('doses'), boosters = this.value('reforcos');
      const model = this.choice('modelo');
      const cards = this.products.map(product => {
        const result = carteira({ pacientes, meses, capacity: product.capacity, firstYear: first, boosters });
        const base = safePick(product.variants, result.need, this.data.tolerance, this.excluded);
        const pick = product.role === model ? this.chosen(product, result.need) : base;
        return { product, result, pick, base };
      }).filter(card => card.pick && card.base);
      this.text('need', new Intl.NumberFormat('pt-BR').format(Math.round(pacientes * meses)));
      this.text('months', decimal(meses, 0) + ' ' + plural(meses, 'mês', 'meses'));
      this.querySelectorAll('[data-card]').forEach(node => {
        const available = cards.some(card => card.product.role === node.dataset.card);
        node.hidden = !available;
        node.querySelector('input').disabled = !available;
      });
      if (!cards.length) { this.clearCart(); return; }
      const minTotal = Math.min(...cards.map(card => card.base.variant.price));
      const maxYears = Math.max(...cards.map(card => Math.min(card.result.years, 99)));
      this.text('need', new Intl.NumberFormat('pt-BR').format(Math.round(pacientes * meses)));
      this.text('months', decimal(meses, 0) + ' ' + plural(meses, 'mês', 'meses'));
      for (const card of cards) {
        const { product, result, pick } = card;
        const { variant } = pick;
        const root = this.querySelector('[data-card="' + product.role + '"]');
        if (!root) continue;
        const set = (name, value) => { const node = root.querySelector('[data-card-out="' + name + '"]'); if (node && node.textContent !== value) node.textContent = value; };
        set('run', runLabel(variant));
        set('total', money(variant.price));
        set('unit', money(variant.price / variant.count) + ' por unidade');
        set('years', yearsLabel(result.years));
        set('slot', money(perSlot(variant.price, variant.count, product.capacity)) + ' por espaço de registro');
        root.querySelector('[data-badge="total"]').hidden = card.base.variant.price !== minTotal;
        root.querySelector('[data-badge="years"]').hidden = Math.min(result.years, 99) !== maxYears;
      }
      const selected = cards.find(card => card.product.role === model) || cards[0];
      this.setChoice('modelo', selected.product.role);
      this.setCart(selected.product, selected.pick.variant, selected.pick.short);
      this.text('model', selected.product.name);
      this.renderOffer(selected.product, selected.pick.variant);
    }

    clearCart() {
      this.current = null;
      const kit = this.querySelector('pg-kit');
      if (kit) { kit.hidden = true; kit.querySelector('[data-kit-item]').value = ''; kit.querySelector('[data-kit-add]').disabled = true; }
      this.show('offer', false);
      this.show('unavailable', true);
      const link = this.querySelector('[data-out-link="product"]');
      if (link) { link.hidden = true; link.removeAttribute('href'); }
    }

    renderComparativo() {
      const quantity = Number(this.choice('quantidade'));
      for (const product of this.products) {
        const raw = runs(product.variants).find(item => (product.role?.startsWith('impressora') && item.unit === 'folha' ? item.count / product.perBlock : item.count) === quantity && item.available !== false && !this.excluded.includes(item.count));
        const variant = sheetVariant(product, raw);
        const cells = this.querySelectorAll('[data-col="' + product.role + '"]');
        cells.forEach(cell => {
          const kind = cell.dataset.cell;
          let value = '';
          if (!variant) value = kind === 'total' ? 'Não vendido nesta quantidade' : '—';
          else if (kind === 'total') value = money(variant.price);
          else if (kind === 'unit') value = money(variant.price / variant.count) + ' por ' + variant.unit;
          else if (kind === 'sheet') value = product.perBlock ? money(variant.price / variant.count / (variant.unit === 'folha' ? 1 : product.perBlock)) + ' por folha' : '—';
          else if (kind === 'slot') value = product.capacity ? money(perSlot(variant.price, variant.count, product.capacity)) : '—';
          if (kind === 'choose') {
            const link = cell.querySelector('a');
            if (link) { if (variant) { link.href = product.url + '?variant=' + variant.id; link.removeAttribute('aria-disabled'); } else { link.removeAttribute('href'); link.setAttribute('aria-disabled', 'true'); } }
            return;
          }
          if (cell.textContent !== value) cell.textContent = value;
        });
      }
      this.text('quantity', runLabel({ count: quantity, unit: this.data.unit || 'unidade', plural: this.data.unitPlural || 'unidades' }));
    }

    syncURL() {
      const url = new URL(location.href);
      this.querySelectorAll('input[data-field][data-param]').forEach(input => url.searchParams.set(input.dataset.param, String(this.value(input.dataset.field))));
      this.querySelectorAll('[data-choice-param]').forEach(group => {
        const value = group.querySelector('input:checked')?.value;
        if (value) url.searchParams.set(group.dataset.choiceParam, value);
      });
      history.replaceState(history.state, '', url);
    }

    async share(button) {
      this.syncURL();
      const url = location.href;
      const status = this.querySelector('[data-share-status]');
      this.metric('share', {});
      try {
        if (navigator.share && matchMedia('(pointer:coarse)').matches) { await navigator.share({ title: document.title, url }); return; }
        await navigator.clipboard.writeText(url);
        if (status) { status.textContent = 'Link copiado.'; clearTimeout(this.shareTimer); this.shareTimer = setTimeout(() => { status.textContent = ''; }, 3000); }
      } catch (error) {
        if (error && error.name === 'AbortError') return;
        if (status) status.textContent = 'Não foi possível copiar. Copie o endereço da barra do navegador.';
      }
      button.blur?.();
    }

    metric(name, detail) {
      try {
        if (root.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.()) Promise.resolve(root.Shopify.analytics?.publish?.('petgraf_tool_' + name, { tool: this.kind, ...detail })).catch(() => {});
      } catch {}
    }
  });

})(typeof window !== 'undefined' ? window : globalThis);
